import { clone, deepAssign } from "@lib/utils/functions";
import LocalStorageManagerBase from "@lib/utils/LocalStorageManager";
import { defineStore } from "pinia";
import config from "src/lib/config/base";
import { jit } from "src/lib/func";
import UserConfig, { TabConfig } from "src/lib/types/config";
import { DeepPartial, Entry, PropType } from "src/lib/types/utils";
import liveAPI from "./LiveAPI";

const getCurrentTimeNs = () =>
    Math.round(Date.now() * 1000000);

function setListeners(fn: () => void) {
    fn();
    window.addEventListener("resize", fn);
}

type Auth = AuthManagerBase & {
    token: string
    getMyData(): Promise<object>
}

class LocalStorageManager extends LocalStorageManagerBase {
    getKeys() {
        return Object.keys(this.settings);
    }
}

let getStorage: (k?: string) => LocalStorageManager = jit(
    (key?) => new LocalStorageManager(key || "core-integrity"));

type SettingValue = number | string | boolean | object;

export type Config = {
    initialized: boolean,
    mobile: boolean,
    authManager: Auth | null,
    currentTab: TabConfig | null
    initActions: string[],
    alertsStatus: {enable: boolean},
    initFunctions: (() => Promise<any>)[],
    api: {
        updateInterval: number
    }
    app: {
        name?: string
        version?: string,
        changelog?: string
        apiUrl: string,
        liveUrl: string,
        docsUrl?: string,
        localStorageKey?: string
        display: {
            feedback: boolean,
            userMenu: boolean
        }
        feedbackLabels?: string[]
    },
    timezone: number,
    localSettings: {
        [key in string]: SettingValue
    } & {
        notifyNewEvent?: boolean
    },
    tabGroups: {[key in string]: {label: string}}
} & UserConfig

const initState: Config = {
    initialized: false,
    alertsStatus: {enable: false},
    mobile: false,
    authManager: null,
    currentTab: null,
    initActions: [],
    api: {
        updateInterval: 10 // min
    },
    app: {
        name: "CoreIntegrity",
        apiUrl: "",
        liveUrl: "",
        localStorageKey: "liveSettings",
        display: {
            feedback: true,
            userMenu: true
        }
    },
    map: {
        initCenter: [30, 0],
        initZoom: 4,
        maxZoom: 32,
        tileSources: {
            base: {
                url: "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
                maxZoom: 20,
                preload: 20,
                type: "OSM"
            },
            satellite: {
                maxZoom: 20,
                preload: 20,
                url: "https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?" +
                "access_token=pk.eyJ1IjoidmFzeWE2NjYiLCJhIjoiY2txa2JvZm44MDJjMjJvbzJ5aDg1ZTAycSJ9.PYNlvhVHyLq8njxjuM3Vxw",
                type: "XYZ"
            }
        }
    },
    timezone: 0,
    localSettings: {},
    sources: [],
    ...config
};

export type ConfigState = typeof initState;

type Tabs = Config['tabs']

export default defineStore("config-live", {
    state: () => initState,
    getters: {
        settings: state => <T = unknown>(key: string) =>
            state.localSettings[key] as T,
        enabledTabs: (state): Entry<Tabs>[] =>
            Object.entries(state.tabs)
                .filter(([_, {isVisible, label, access, userAccess}]: [string, TabConfig]) =>
                    (label && (
                        isVisible === undefined ||
                        typeof isVisible == "function" && isVisible()) && (
                        access != "restricted" && userAccess !== false || userAccess
                    ))),
        visibleTabs(state): {
            [tab in keyof Tabs]: PropType<Tabs> &
                                 {label: string, tabGroup: boolean}
        }
        {
            // It is not possible to type properly in options store
            // And it is not possible to refactor to setup store,
            // coz config state spread syntax...
            const enabledTabs = this.enabledTabs as unknown as Entry<Tabs>[];
            //@ts-ignore
            return enabledTabs
                .reduce(
                    //@ts-ignore
                    (d: Tabs, [key, conf]: Entry<Tabs>) => ({
                        ...d,
                        ...(conf.group ?
                            {[conf.group]: {
                                ...state.tabGroups[conf.group],
                                tabGroup: true
                            }}
                            : {[key]: conf})
                    }),
                    {}
                );
        },
        groupTabs() {
            return (tab: string): Tabs =>
                this.tabGroups[tab] ?
                    this.enabledTabs
                        .filter(
                            ([,{group}]) => group === tab)
                        .reduce(
                            (d, [k, v]) => ({...d, [k]: v}),
                            {} as Tabs
                        )
                    : {};
        }
    },
    actions: {
        setMobile() {
            this.mobile = window.innerWidth <= 800;
        },
        initialize({config, authManager}: {
            config: DeepPartial<ConfigState>,
            authManager: Auth
        }) {
            this.populate(config);
            setListeners(() => this.setMobile());
            liveAPI.setApiUrl(this.app.apiUrl);
            liveAPI.setAuthManager(authManager);
            this.authManager = authManager;

            return Promise.resolve();
        },
        setTabs(tabs: UserConfig['tabs'], tabGroups: UserConfig['tabGroups'] = {}) {
            this.tabs = tabs;
            this.tabGroups = tabGroups;
            this.setTab(tabs[this.startTab] || Object.values(tabs)[0]);
        },
        load() {
            return Promise.all([
                this.syncTime(),
                this.loadConfig(),
                ...this.initFunctions.map(fn => fn())
            ])
                .then(() => this.initTabs())
                .then(() => this.initialized = true);
        },
        loadConfig() {
            return liveAPI.getConfig()
                .then(config => {
                    this.populate(config);
                    const storage = getStorage(this.app.localStorageKey);
                    storage.getKeys().forEach(k => {
                        if(k.startsWith("config:")) {
                            deepAssign(this, storage.get(k));
                        }
                    });
                });
        },
        initTabs() {
            const config: {
                tabs: {[key in string]: {tabSelect: TabConfig['tabSelect']}}
            } = {tabs: {}};

            Object.entries(this.tabs)
                .forEach(([tab, conf]: [string, TabConfig]) => {
                    if(conf.generateTabSelect) {
                        config.tabs[tab] = {
                            tabSelect: Object.fromEntries(
                                Object.entries(conf.generateTabSelect)
                                    .map(([k, fn]) => [k, fn()])
                            )
                        };
                    }
                });
            return this.populate(config);
        },
        syncTime() {
            Date.prototype.timeDiff = 0;
            const reqTime = getCurrentTimeNs();
            return liveAPI.getServerTime(reqTime)
                .then(({server_time_ns, time_diff}) => {
                    const resTime = getCurrentTimeNs();
                    const time_diff2 = resTime - server_time_ns;
                    const response = reqTime - resTime
                                     + time_diff - time_diff2;
                    const syncTime = time_diff - response;
                    Date.prototype.timeDiff = syncTime / 1e6;
                });
        },
        setTab(tab: TabConfig) {
            this.currentTab = tab;
        },
        populate(this: Config, config: DeepPartial<ConfigState>) {
            deepAssign(this, config);
            // assign properties that are needed, but too unreliable for
            // setting manually
            for(let tab in this.tabs) {
                const tObj = this.tabs[tab];
                const sources: {[key: string]: 1} = {};
                for(let comp in tObj.components) {
                    let cObj = tObj.components[comp];
                    if(cObj.template && this.templates[cObj.template]) {
                        tObj.components[comp] = deepAssign(
                            clone(this.templates[cObj.template]),
                            tObj.components[comp]
                        );
                        cObj = tObj.components[comp];
                    }
                    if(cObj.props?.sources) {
                        cObj.props?.sources.forEach(
                            ({source}) => sources[source] = 1);
                    }
                }
                tObj.sources = Object.keys(sources);
            }
            this.tabs = {...this.tabs};
        },
        storeConfUpdate(key: string, update: object) {
            deepAssign(this, update);
            getStorage(this.app.localStorageKey)
                .write("config:" + key, update);
        },
        getAppInfo() {

        },
        getChangelog() {

        },
        storeSettings({key, value}: {key: string, value: SettingValue}) {
            this.localSettings[key] = value;
            getStorage(this.app.localStorageKey).write(key, value);
        },
        loadSettings({key, defaultValue}: {
            key: string,
            defaultValue: SettingValue
        }) {
            
            if(getStorage(this.app.localStorageKey) == null) return;

            let stored = getStorage(this.app.localStorageKey).get(key);
            if(stored == null) {
                stored = defaultValue;
                getStorage(this.app.localStorageKey).write(key, defaultValue);
            }
            this.localSettings = {
                ...this.localSettings,
                [key]: stored
            };
        }
    }
});